import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "hooks/useQuery";
import { getIndividualBrokerHistoricalCharts } from "../service";

const DEFAULT_POLL_INTERVAL_MS = 5000;
const MAX_AUTO_POLL_ATTEMPTS = 20;
const POLLING_CACHE_STATUSES = new Set(["processing", "refreshing"]);

const normalizeStatus = (status) =>
  typeof status === "string" ? status.toLowerCase() : "unknown";

const csvToIdList = (csv) =>
  (csv || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

export const useIndividualBrokerHistoricalCharts = (
  param = {},
  options = {}
) => {
  const {
    enabled = true,
    autoPoll = true,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    staleTime,
    cacheTime,
  } = options;

  const selectedIdsCsv = param?.["broker_account_ids"] || "";
  const selectedIds = useMemo(
    () => csvToIdList(selectedIdsCsv),
    [selectedIdsCsv]
  );
  const hasSelectedIds = selectedIds.length > 0;

  const queryClient = useQueryClient();

  const [mergedData, setMergedData] = useState({});
  const pendingFetchIdsRef = useRef([]);
  const selectedIdsRef = useRef(selectedIds);
  const pollAttemptRef = useRef(0);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
    pendingFetchIdsRef.current = [...selectedIds];
    pollAttemptRef.current = 0;
    setMergedData((prev = {}) => {
      const filtered = {};
      selectedIds.forEach((id) => {
        if (prev[id]) filtered[id] = prev[id];
      });
      if (Object.keys(filtered).length === Object.keys(prev).length) {
        return prev;
      }
      return filtered;
    });
  }, [selectedIds]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const queryKey = useMemo(
    () => ["individualBrokerHistoricalCharts", selectedIdsCsv],
    [selectedIdsCsv]
  );

  const {
    isLoading,
    isFetching,
    data: queryData,
    error,
    refetch: queryRefetch,
  } = useQuery({
    key: "individualBrokerHistoricalCharts",
    queryKey,
    queryFn: async () => {
      const idsForRequest =
        pendingFetchIdsRef.current?.length > 0
          ? pendingFetchIdsRef.current
          : selectedIdsRef.current;

      if (!idsForRequest.length) {
        return {};
      }

      const response = await getIndividualBrokerHistoricalCharts({
        broker_account_ids: idsForRequest.join(","),
      });

      // Polls fetch only the still-processing ids, so merge with existing cache
      // to avoid overwriting the full dataset with a partial response.
      const existing = queryClient.getQueryData(queryKey) || {};
      return { ...existing, ...(response || {}) };
    },
    enabled: enabled && hasSelectedIds,
    staleTime,
    cacheTime,
  });

  useEffect(() => {
    if (!queryData || typeof queryData !== "object") {
      return;
    }

    const normalizedLatest = Object.entries(queryData || {}).reduce(
      (acc, [accountId, payload]) => {
        acc[String(accountId)] = payload;
        return acc;
      },
      {}
    );
    setMergedData((prev = {}) => {
      const next = {};
      selectedIdsRef.current.forEach((id) => {
        if (normalizedLatest[id]) {
          next[id] = normalizedLatest[id];
        } else if (prev[id]) {
          next[id] = prev[id];
        }
      });
      return next;
    });

    pendingFetchIdsRef.current = [];
  }, [queryData]);

  const statusSummary = useMemo(() => {
    const summary = {
      byAccount: {},
      cacheStatusCounts: {},
      hydrationStatusCounts: {},
      hasPollingStatus: false,
    };

    Object.entries(mergedData || {}).forEach(([accountId, payload]) => {
      const cacheStatus = normalizeStatus(payload?.cache_status);
      const hydrationStatus = normalizeStatus(payload?.hydration_status);

      summary.byAccount[accountId] = {
        cache_status: cacheStatus,
        hydration_status: hydrationStatus,
        raw: payload,
      };

      summary.cacheStatusCounts[cacheStatus] =
        (summary.cacheStatusCounts[cacheStatus] || 0) + 1;

      summary.hydrationStatusCounts[hydrationStatus] =
        (summary.hydrationStatusCounts[hydrationStatus] || 0) + 1;

      if (
        selectedIdSet.has(accountId) &&
        POLLING_CACHE_STATUSES.has(cacheStatus)
      ) {
        summary.hasPollingStatus = true;
      }
    });

    return summary;
  }, [mergedData, selectedIdSet]);

  const pollTimeoutRef = useRef(null);
  const resolvedPollInterval =
    Number.isFinite(pollIntervalMs) && pollIntervalMs > 0
      ? pollIntervalMs
      : DEFAULT_POLL_INTERVAL_MS;

  const pollableAccountIds = useMemo(() => {
    if (!selectedIds.length) return [];
    return selectedIds.filter((id) => {
      const cacheStatus = normalizeStatus(mergedData?.[id]?.cache_status);
      return POLLING_CACHE_STATUSES.has(cacheStatus);
    });
  }, [selectedIds, mergedData]);

  const pollableSignature = pollableAccountIds.join(",");

  const shouldPoll =
    autoPoll &&
    enabled &&
    pollableAccountIds.length > 0 &&
    pollAttemptRef.current < MAX_AUTO_POLL_ATTEMPTS;

  // Polling loop: every interval, refetch only the still-processing accounts until all settle.
  useEffect(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    if (!shouldPoll || isFetching) {
      return;
    }

    pendingFetchIdsRef.current = [...pollableAccountIds];
    pollAttemptRef.current += 1;

    pollTimeoutRef.current = setTimeout(() => {
      queryRefetch();
      pollTimeoutRef.current = null;
    }, resolvedPollInterval);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [
    shouldPoll,
    pollableSignature,
    isFetching,
    queryRefetch,
    resolvedPollInterval,
  ]);

  const refetchAll = () => {
    pendingFetchIdsRef.current = [...selectedIdsRef.current];
    pollAttemptRef.current = 0;
    return queryRefetch();
  };

  return {
    isLoading,
    isFetching,
    data: mergedData,
    error,
    refetch: refetchAll,
    statusSummary,
    isPolling: shouldPoll,
  };
};
